import { Box, Center, Text } from "@chakra-ui/react";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useWindowSize } from "react-use";

import { pxToRem } from "adminComponents/utils/pxToRem";
import {
  CoverImageBGColorSchemeType,
  CoverImageBGPatternType,
  CoverImageIconType,
  ISessionUser,
  SessionGameType,
} from "links/lib/types";
import { useBreakpoints } from "sessionComponents/contexts/breakpoints";
import { useBoundingRect } from "sessionComponents/hooks/useBoundingRect";
import { AvatarLayout } from "sessionComponents/molecules/AvatarLayout";
import { LobbyLayout } from "sessionComponents/molecules/LobbyLayout";
import { PracticeSetCard } from "sessionComponents/molecules/PracticeSetCard";

import { useDetectOrientation } from "../../theme/hooks/useDetectOrientation";

export interface IStudentLobbyProps {
  outerGame?: SessionGameType;
  student: ISessionUser;
  practiceSet: {
    coverImageBGColorScheme: CoverImageBGColorSchemeType;
    coverImageIcon: CoverImageIconType;
    coverImageBGPattern: CoverImageBGPatternType;
    practiceSetName: string;
    teacherTitle: string;
    className: string;
  };
}

export const StudentLobby: React.FC<IStudentLobbyProps> = ({
  outerGame = SessionGameType.TheBigBoard,
  student,
  practiceSet,
}) => {
  const { match: currentBreakpoints } = useBreakpoints();
  const { isPortrait } = useDetectOrientation();
  const { t } = useTranslation("session", {
    keyPrefix: "studentLobby",
    useSuspense: false,
  });
  const [practiceSetCardRef, { height: practiceSetCardHeight }] =
    useBoundingRect<HTMLDivElement>();
  const { height: windowHeight } = useWindowSize();
  const isQuickPlay = outerGame === SessionGameType.QuickPlay;

  const practiceSetCard = (
    <Box position="relative" flexGrow={2} ref={practiceSetCardRef}>
      <PracticeSetCard
        coverImageBGColorScheme={practiceSet.coverImageBGColorScheme}
        coverImageBGPattern={practiceSet.coverImageBGPattern}
        coverImageIcon={practiceSet.coverImageIcon}
        practiceSetName={practiceSet.practiceSetName}
        teacherTitle={practiceSet.teacherTitle}
        className={practiceSet.className}
      />
    </Box>
  );

  const hideAvatar = useMemo(
    () => isPortrait && practiceSetCardHeight / windowHeight > 0.6,
    [isPortrait, practiceSetCardHeight, windowHeight]
  );

  const avatarStage = (
    <>
      <AvatarLayout students={[student]} selfId={student.id} />

      <Box w="100%" bottom="75px" position="relative">
        <Center>
          <Text
            fontSize={pxToRem(currentBreakpoints.fontSize)}
            color="overlay.white"
            textStyle="gameText"
          >
            {t("avatarMessage")}
          </Text>
        </Center>
      </Box>
    </>
  );

  return (
    <>
      {!isQuickPlay && (
        <LobbyLayout
          avatarComponent={hideAvatar ? <></> : avatarStage}
          actionContent={practiceSetCard}
          stageProps={{ showCurtainOnly: isPortrait }}
        />
      )}

      {isQuickPlay && (
        <LobbyLayout
          avatarComponent={hideAvatar ? <></> : avatarStage}
          actionContent={practiceSetCard}
          stageProps={{ showCurtainOnly: isPortrait, outerGame }}
        />
      )}
    </>
  );
};
